import { Directive, inject, input, OnInit, TemplateRef, ViewContainerRef } from '@angular/core';
import { PermissionsService } from '../../core/permissions/permissions.service';
import { takeUntil } from 'rxjs/operators';
import { PermissionsDto } from '../../core/permissions/interfaces/permissions.interface';
import { Subject } from 'rxjs';
import { Role } from '../../core/permissions/roles.enum';

@Directive({
  standalone: true,
  selector: '[appElementEdit]'
})
export class CanEditDirective implements OnInit {
  elementEdit = input.required<keyof PermissionsDto | 'none'>({ alias: 'appElementEdit' });

  private destroy$ = new Subject<void>();
  private hasView = false;
  private templateRef = inject(TemplateRef);
  private viewContainer = inject(ViewContainerRef);
  private permissionsService = inject(PermissionsService);
  private role: Role;

  constructor() {}

  ngOnInit(): void {
    this.permissionsService.role$.pipe(takeUntil(this.destroy$)).subscribe((role: Role | null) => {
      if (role === Role.SHOP_OWNER || role === Role.SHOP_SPY) {
        this.role = role;
      }
    });

    if (this.elementEdit() === 'none') {
      // 'none' contract: render only for merchant-wide privileged roles
      // (SHOP_OWNER, SHOP_SPY); hide for SHOP_COLLABORATOR and while role is
      // unresolved. Never touches permissions[...].
      this.permissionsService.role$
        .pipe(takeUntil(this.destroy$))
        .subscribe((role: Role | null) => {
          const privileged = role === Role.SHOP_OWNER || role === Role.SHOP_SPY;
          if (privileged && !this.hasView) {
            this.viewContainer.createEmbeddedView(this.templateRef);
            this.hasView = true;
          } else if (!privileged && this.hasView) {
            this.viewContainer.clear();
            this.hasView = false;
          }
        });
      return;
    }

    this.permissionsService.permissions$.pipe(takeUntil(this.destroy$)).subscribe(permissions => {
      if (!permissions && (this.role === Role.SHOP_OWNER || this.role === Role.SHOP_SPY)) {
        this.viewContainer.createEmbeddedView(this.templateRef);
        this.hasView = true;
        return;
      }

      if (!permissions) {
        this.viewContainer.clear();
        return;
      }

      const modulePermission = permissions[this.elementEdit()];

      if (modulePermission.edit) {
        if (!this.hasView) {
          this.viewContainer.createEmbeddedView(this.templateRef);
          this.hasView = true;
        }
      } else {
        this.viewContainer.clear();
        this.hasView = false;
      }
    });
  }

  ngOnDestroy(): void {
    this.destroy$.next();
    this.destroy$.complete();
  }
}
